// 공개 첫 화면(홈페이지 시안 3) — 세션 여부는 page.tsx 가 정하고 여기는 받은 값으로 그리기만 한다(테스트가 그대로 그린다).

import { Fragment, type ComponentType } from "react";
import { AppWindow, ArrowRight, Check, FileText, History, Link2, QrCode, Smartphone, SquareCheck } from "lucide-react";

import { Link } from "@/lib/nav";

import SectionCard from "@buildpy/ui/section-card";
import Pill from "@buildpy/ui/pill";
import { Button } from "@buildpy/ui/button";
import { BrandIcon } from "@buildpy/ui/brand-logo";
import { cn } from "@/lib/utils";
import { AFTER, BOARD, CLOSING, FAQ, FAQ_TITLE, FLOW, HERO, HERO_FACTS, PAIN, PRICING, STEPS, type FlowIcon, type PainScene } from "@buildpy/core/modules/landing/content";
import { DEMO_PATH } from "@buildpy/core/modules/demo/routes";
import { SUPPORT_EMAIL } from "@buildpy/core/lib/support-email";
import {
  BoardMock,
  CaseMock,
  ChangeMock,
  ContractSignMock,
  DailyMock,
  HeroBoardMock,
  InboxMock,
  MOCK_CARD,
  PainCall,
  PainLaptop,
  PainLock,
  PainPhotos,
  QuoteRevisionMock,
  StepBuild,
  StepContract,
  StepInquiry,
  StepQuote,
} from "@/modules/landing/ui/mockups";
import { PublicFooter, PublicHeader } from "@/modules/landing/ui/public-header";
import { PUBLIC_CONTAINER as CONTAINER } from "@/modules/landing/ui/public-header-state";
import { LandingPlanCards } from "@/modules/plan/ui/plan-cards";
import { APP_CTA, SIGNUP_CTA } from "@/modules/plan/copy";

export interface LandingViewProps {
  signedIn: boolean;
}

const H2 = "text-4xl font-extrabold leading-tight tracking-tight md:text-6xl";
const EYEBROW = "text-md font-bold text-primary";
const LEAD = "text-lg leading-relaxed text-secondary-foreground md:text-xl";

const PAIN_SCENE: Record<PainScene, ComponentType> = { laptop: PainLaptop, call: PainCall, photos: PainPhotos, lock: PainLock };
// 원화 기호는 lucide 에 없어 글자로 그린다.
const Won = ({ className }: { className?: string }) => <span className={cn("font-bold leading-none", className)}>₩</span>;
const FLOW_ICON: Record<FlowIcon, ComponentType<{ className?: string }>> = {
  browser: AppWindow,
  link: Link2,
  qr: QrCode,
  history: History,
  template: FileText,
  won: Won,
  file: FileText,
  check: SquareCheck,
  smartphone: Smartphone,
};
const STEP_MOCKS = [StepInquiry, StepQuote, StepContract, StepBuild] as const;
const FLOW_MOCKS = [InboxMock, QuoteRevisionMock, ContractSignMock] as const;
const AFTER_MOCKS = [DailyMock, ChangeMock, CaseMock] as const;

const Lines = ({ lines }: { lines: readonly string[] }) =>
  lines.map((line, i) => (
    <Fragment key={line}>
      {i > 0 && <br />}
      {line}
    </Fragment>
  ));

// 로그인 상태면 머리와 같은 규칙으로 가입 대신 「내 현장으로」.
const SignupCta = ({ idPrefix, signedIn, onPrimary = false }: { idPrefix: string; signedIn: boolean; onPrimary?: boolean }) => (
  <span className="contents" data-testid={`${idPrefix}-signup`}>
    <Button asChild className="min-h-14" size="cta" variant={onPrimary ? "inverse" : "primary"}>
      {signedIn ? (
        <Link data-testid={`${idPrefix}-app-link`} href={APP_CTA.href}>
          {APP_CTA.label}
        </Link>
      ) : (
        <Link data-testid={`${idPrefix}-signup-link`} href={SIGNUP_CTA.href}>
          {SIGNUP_CTA.label}
          {!onPrimary && <ArrowRight aria-hidden />}
        </Link>
      )}
    </Button>
  </span>
);

const StepFlow = () => (
  <ol className="flex flex-col rounded-lg bg-accent p-5 md:flex-row md:items-stretch md:p-9">
    {STEPS.map((s, i) => {
      const Mock = STEP_MOCKS[i];
      return (
        <Fragment key={s.label}>
          {i > 0 && (
            <li aria-hidden className="hidden items-center justify-center px-3 pt-9 text-primary md:flex">
              <ArrowRight className="size-5" />
            </li>
          )}
          <li className="relative flex min-w-0 flex-1 flex-col gap-3 pb-6 pl-10 last:pb-0 md:pb-0 md:pl-0">
            {/* 폰에서는 번호 사이를 세로줄로 잇는 타임라인 */}
            {i < STEPS.length - 1 && <span aria-hidden className="absolute bottom-0.5 left-3.5 top-8 w-0.5 rounded-full bg-primary/40 md:hidden" />}
            <span className="flex min-h-7 flex-wrap items-center gap-x-2 text-md font-bold text-primary">
              <span className="absolute left-0 top-0 flex size-7 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground md:static md:size-6">{i + 1}</span>
              {s.label}
              <span className="font-medium text-secondary-foreground md:hidden">{s.cap}</span>
            </span>
            <div className={cn(MOCK_CARD, "p-5 md:h-52")}>
              <Mock />
            </div>
          </li>
        </Fragment>
      );
    })}
  </ol>
);

type PainItem = (typeof PAIN.items)[number];

// 흩어진 한 곳 — 잠금 화면만 밤 화면처럼 어두운 레일 바탕에 그린다(다크는 바탕과 같은 색이라 테두리를 border 로 올린다).
const PainCard = ({ item }: { item: PainItem }) => {
  const Scene = PAIN_SCENE[item.scene];
  const caption = `${item.what} ${item.where}`;
  if (item.scene === "lock") {
    return (
      <li className="flex flex-col gap-3 rounded-lg border border-rail-border bg-rail p-4 shadow-lg dark:border-border" data-testid="landing-pain-card">
        <Scene />
        <p className="mt-auto text-lg font-bold text-rail-strong">{caption}</p>
      </li>
    );
  }
  return (
    <SectionCard as="li" className="flex flex-col gap-3 p-4 shadow-lg" data-testid="landing-pain-card" padding="none">
      <Scene />
      <p className="mt-auto text-lg font-bold">{caption}</p>
    </SectionCard>
  );
};

// 넓은 화면 세로 간격 — 카드 칸과 점선 칸이 같은 값을 써야 점선이 카드 가운데에 온다.
const PAIN_ROW_GAP = "lg:gap-7";
// 칸 폭은 본문 폭 비율(시안 1104px 기준 카드 300 = 25/92 · 허브 360 = 15/46)이라 폭 구간 경계에서 튀지 않는다 — 두 줄 같은 높이(1fr)라 양쪽 카드 높이가 같다.
const PAIN_COLUMN = cn("grid w-full gap-3 sm:grid-cols-2 lg:w-25/92 lg:shrink-0 lg:grid-cols-1 lg:grid-rows-2", PAIN_ROW_GAP);
const PAIN_DASH_ROW = "flex flex-1 items-center";
const PAIN_DASH = "w-full border-t-2 border-dashed border-primary/40";

// 양옆 네 곳이 가운데 현장 하나로 모이는 점선 — 넓은 화면에서만, 카드 칸과 같은 두 줄로 나눠 각 줄 가운데에 긋는다.
const PainDashes = ({ className }: { className: string }) => (
  <div aria-hidden className={cn("hidden min-w-0 flex-1 flex-col px-2 lg:flex", PAIN_ROW_GAP, className)}>
    <span className={PAIN_DASH_ROW}>
      <span className={PAIN_DASH} />
    </span>
    <span className={PAIN_DASH_ROW}>
      <span className={PAIN_DASH} />
    </span>
  </div>
);

const PainHub = () => (
  <div
    className="flex w-full max-w-sm flex-col gap-3 rounded-lg border-2 border-primary bg-card p-6 shadow-lg lg:order-3 lg:w-15/46 lg:max-w-none lg:shrink-0 lg:self-center"
    data-testid="landing-pain-hub">
    <span className="flex items-center gap-2 text-md font-bold text-primary">
      <BrandIcon decorative />
      {PAIN.hub.label}
    </span>
    <strong className="text-2xl font-extrabold leading-snug">{PAIN.hub.site}</strong>
    <ul className="flex flex-col gap-2 text-md">
      {PAIN.hub.checks.map((c) => (
        <li key={c} className="flex items-start gap-2">
          <Check aria-hidden className="mt-1 size-4 shrink-0 text-primary" />
          {c}
        </li>
      ))}
    </ul>
    <p className="border-t border-border pt-3 text-xl font-bold text-primary">{PAIN.hub.outro}</p>
  </div>
);

const LandingView = ({ signedIn }: LandingViewProps) => (
  <div className="min-h-screen bg-card text-lg leading-relaxed text-foreground [word-break:keep-all]">
    <PublicHeader onLanding signedIn={signedIn} />

    <main>
      <section className="scroll-mt-safe-16 overflow-hidden" id="top">
        <div className={cn(CONTAINER, "flex flex-col items-center pb-16 pt-14 text-center md:pb-24 md:pt-22 lg:flex-row lg:items-center lg:gap-14 lg:pb-16 lg:pt-16 lg:text-left")}>
          <div className="flex flex-col items-center lg:w-120 lg:shrink-0 lg:items-start">
            <Pill className="px-4 py-1.5 text-md" tone="primary">
              {HERO.eyebrow}
            </Pill>
            <h1 className="mt-6 text-6xl font-extrabold leading-tight tracking-tight md:text-9xl lg:text-8xl">
              {HERO.title[0]}
              <br />
              <span className="text-primary">{HERO.title[1]}</span>
            </h1>
            <p className={cn(LEAD, "mt-6")}>{HERO.body}</p>
            <div className="mt-9 flex flex-wrap justify-center gap-3 lg:justify-start">
              <SignupCta idPrefix="hero" signedIn={signedIn} />
              <Button asChild className="min-h-14" size="cta" variant="card">
                <Link data-testid="landing-demo-link" href={DEMO_PATH.home}>
                  {HERO.demo}
                </Link>
              </Button>
            </div>
            <ul className="mt-6 flex flex-col gap-1.5 text-md text-secondary-foreground" data-testid="landing-hero-checks">
              {HERO.checks.map((c) => (
                <li key={c} className="flex items-center gap-2">
                  <Check aria-hidden className="size-4 shrink-0 text-primary" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative hidden min-w-0 flex-1 lg:block">
            <HeroBoardMock />
          </div>
        </div>
      </section>

      <div className="border-y border-border bg-background">
        <ul className={cn(CONTAINER, "grid grid-cols-2 gap-x-4 gap-y-3 py-6 text-center text-md text-secondary-foreground lg:grid-cols-4 lg:text-left")} data-testid="landing-hero-facts">
          {HERO_FACTS.map((f) => (
            <li key={f.strong}>
              <strong className="font-bold text-foreground">{f.strong}</strong>
              {f.rest}
            </li>
          ))}
        </ul>
      </div>

      <section className="bg-background">
        <div className={cn(CONTAINER, "py-16 md:py-24")}>
          <h2 className="text-center text-3xl font-extrabold tracking-tight md:text-5xl">{PAIN.title}</h2>
          {/* 읽는 순서 = 폰에서 보이는 순서(네 곳 → 세로 점선 → 현장 하나), 넓은 화면은 lg:order 로만 양옆에서 가운데로 모은다 */}
          <div className="mt-10 flex flex-col items-center lg:flex-row lg:items-stretch">
            <ul className={cn(PAIN_COLUMN, "lg:order-1")}>
              {PAIN.items.slice(0, 2).map((p) => (
                <PainCard key={p.scene} item={p} />
              ))}
            </ul>
            <PainDashes className="lg:order-2" />
            <ul className={cn(PAIN_COLUMN, "mt-3 lg:order-5 lg:mt-0")}>
              {PAIN.items.slice(2).map((p) => (
                <PainCard key={p.scene} item={p} />
              ))}
            </ul>
            <PainDashes className="lg:order-4" />
            <span aria-hidden className="h-10 border-l-2 border-dashed border-primary/40 lg:hidden" />
            <PainHub />
          </div>
        </div>
      </section>

      <div className={cn(CONTAINER, "py-16 md:py-24")}>
        <StepFlow />
      </div>

      <section className="scroll-mt-safe-16 bg-background" id="flow">
        <div className={cn(CONTAINER, "py-16 md:py-28")}>
          <div className="text-center">
            <p className={EYEBROW}>{FLOW.eyebrow}</p>
            <h2 className={cn(H2, "mt-3")}>
              <Lines lines={FLOW.title} />
            </h2>
          </div>
          <div className="mt-12 flex flex-col gap-16 md:mt-20 md:gap-28">
            {FLOW.rows.map((row, i) => {
              const Mock = FLOW_MOCKS[i];
              return (
                <div key={row.eyebrow} className={cn("flex flex-col gap-8 md:items-center md:gap-20", i % 2 === 1 ? "md:flex-row-reverse" : "md:flex-row")}>
                  <div className="min-w-0 md:flex-1">
                    <p className={EYEBROW}>{row.eyebrow}</p>
                    <h3 className="mt-3 text-3xl font-extrabold leading-snug tracking-tight md:text-5xl">
                      <Lines lines={row.title} />
                    </h3>
                    <ul className="mt-7 flex flex-col gap-5">
                      {row.bullets.map((b) => {
                        const Icon = FLOW_ICON[b.icon];
                        return (
                          <li key={b.title} className="flex gap-4">
                            <span aria-hidden className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-accent text-primary">
                              <Icon className="size-5" />
                            </span>
                            <span className="flex flex-col gap-0.5">
                              <strong className="text-xl font-bold">{b.title}</strong>
                              <span className="text-md leading-relaxed text-secondary-foreground md:text-lg">{b.desc}</span>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  <div className="min-w-0 md:flex-1">
                    <Mock />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className={cn(CONTAINER, "scroll-mt-safe-16 py-16 md:py-28")} id="board">
        <div className="text-center">
          <h2 className={H2}>
            <Lines lines={BOARD.title} />
          </h2>
          <p className={cn(LEAD, "mt-4")}>{BOARD.body}</p>
        </div>
        <div className="mt-10 md:mt-14">
          <BoardMock />
        </div>
      </section>

      <section className="bg-rail text-rail-foreground">
        <div className={cn(CONTAINER, "py-16 md:py-28")}>
          <div className="text-center">
            <p className="text-md font-bold text-rail-brand">{AFTER.eyebrow}</p>
            <h2 className={cn(H2, "mt-3 text-rail-strong")}>
              <Lines lines={AFTER.title} />
            </h2>
          </div>
          <ul className="mt-10 grid grid-cols-1 gap-5 md:mt-14 md:grid-cols-3">
            {AFTER.cards.map((c, i) => {
              const Mock = AFTER_MOCKS[i];
              return (
                <li key={c.label} className="flex flex-col rounded-lg bg-rail-active p-6">
                  <Mock />
                  <span className="mt-6 text-sm font-bold text-rail-brand">{c.label}</span>
                  <strong className="mt-1 text-2xl font-bold leading-snug text-rail-strong">
                    <Lines lines={c.text} />
                  </strong>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <div className={cn(CONTAINER, "py-16 md:py-28")}>
        <section className="scroll-mt-safe-24" data-testid="landing-pricing" id="pricing">
          <div className="text-center">
            <h2 className={H2}>{PRICING.title}</h2>
            <p className={cn(LEAD, "mt-4")}>{PRICING.body}</p>
          </div>
          <div className="mt-10">
            <LandingPlanCards signedIn={signedIn} />
          </div>
          <p className="mt-6 text-center text-md text-muted-foreground">{PRICING.note}</p>
        </section>

        <section className="mx-auto mt-20 max-w-3xl scroll-mt-safe-24 md:mt-28" id="faq">
          <h2 className={cn(H2, "text-center")}>{FAQ_TITLE}</h2>
          <div className="mt-8 flex flex-col border-b border-border md:mt-10">
            {FAQ.map((f, i) => (
              <details key={f.q} className="group border-t border-border" open={i === 0}>
                <summary className="flex min-h-11 cursor-pointer items-center py-5 text-xl font-bold">{f.q}</summary>
                <p className="pb-6 text-lg leading-relaxed text-secondary-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      </div>

      {!signedIn && (
        <section className="scroll-mt-safe-24 px-4 pb-16 md:px-6 md:pb-28" id="start">
          <div className="mx-auto flex max-w-7xl flex-col items-center rounded-lg bg-primary px-6 py-16 text-center text-primary-foreground md:py-24">
            <h2 className={H2}>
              <Lines lines={CLOSING.title} />
            </h2>
            <p className="mt-4 text-lg md:text-xl">{CLOSING.body}</p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <SignupCta onPrimary idPrefix="join" signedIn={false} />
              <a
                className="inline-flex min-h-14 items-center justify-center rounded-md border border-primary-foreground/40 px-8 text-lg font-semibold text-primary-foreground hover:bg-primary-foreground/10"
                data-testid="landing-contact-link"
                href={`mailto:${SUPPORT_EMAIL}`}>
                {CLOSING.contact}
              </a>
            </div>
          </div>
        </section>
      )}
    </main>

    <PublicFooter signedIn={signedIn} />
  </div>
);

export default LandingView;
